/**
 * Pure composer document model. Knows nothing about React, react-query,
 * settings hooks, or notifications — it takes formatted inputs and returns the next
 * document. All business decisions about how the body, the single tracked signature
 * region, and snippet attribution combine live here so no caller concatenates composer
 * strings by hand. The reducer only adds, removes, or swaps an untouched managed
 * signature region; user-modified signature text is always demoted to body so no
 * signature action can destroy it.
 */

/**
 * The delimiter that separates the composer body from its tracked signature region.
 * Shared by the signature formatter and the reducer so the two producers cannot drift.
 */
export const SIGNATURE_DELIMITER = '\n---\n';

/**
 * The single tracked signature region and the user's intent for it. Only untouched
 * configured text is managed; once modified, that text becomes body and the signature
 * is dismissed.
 */
export type SignatureState =
    | { kind: 'managed'; sourceKey: string; text: string }
    | { kind: 'dismissed'; sourceKey: string };

export type ComposerDocument = {
    /** Everything above the signature. */
    body: string;
    /** The single tracked signature region and the user's intent for it. */
    signature: SignatureState;
    /** Selection attribution. The latest applied snippet wins; only a send/reset clears it. */
    latestAppliedSnippetId?: number;
};

export type ComposerState = {
    conversationId: string;
    document: ComposerDocument;
    /** Increments for every document change and guards asynchronous send completion. */
    revision: number;
    /** Increments only when a snippet is applied so successful sends consume the right attribution. */
    snippetRevision: number;
};

export type ComposerAction =
    | { type: 'textEdited'; text: string }
    | { type: 'snippetApplied'; snippetId: number; body: string }
    | { type: 'suggestionAccepted'; content: string }
    | { type: 'signatureToggled'; sourceKey: string; managedSignature: string }
    | { type: 'signatureSourceChanged'; sourceKey: string; managedSignature: string }
    | {
        type: 'sendSucceeded';
        conversationId: string;
        revision: number;
        snippetRevision: number;
        sourceKey: string;
        managedSignature: string;
    };

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const signatureText = (signature: SignatureState): string =>
    signature.kind === 'dismissed' ? '' : signature.text;

/** The flattened textarea value. There is no stored copy — it is always derived. */
export const composerText = (state: ComposerState): string =>
    `${state.document.body}${signatureText(state.document.signature)}`;

/** Emptiness inspects only the body, so "just a signature" counts as empty. */
export const isComposerEmpty = (state: ComposerState): boolean =>
    !state.document.body.trim();

/**
 * A composer holding only the untouched managed signature is not sendable. Text typed
 * after or into the signature region is body, so the normal emptiness check makes it
 * sendable.
 */
export const isComposerSendable = (state: ComposerState): boolean =>
    !isComposerEmpty(state);

export const isSignaturePresent = (state: ComposerState): boolean => {
    const { signature } = state.document;
    return signature.kind !== 'dismissed' && signature.text.trim() !== '';
};

export const snippetIdForSend = (state: ComposerState): number | undefined =>
    state.document.latestAppliedSnippetId;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The single place that decides ordering and spacing for inserted content. Snippets and
 * suggestions both prepend as `inserted\n\nexisting`; a blank insertion is a no-op.
 */
export const prependBody = (existing: string, inserted: string): string => {
    const next = inserted.trimEnd();

    if (!next.trim()) {
        return existing;
    }

    return existing.trim() ?
        `${next}\n\n${existing.trimStart()}` :
        next;
};

/**
 * Identifies the channel + configured-signature version that produced a signature region.
 * Equality changes iff either the channel or the formatted signature changes. Its format
 * is an implementation detail; callers pass channel + formatted signature and never parse
 * the result.
 */
export const makeSourceKey = (channel: string, managedSignature: string): string =>
    `${channel}::${managedSignature}`;

export const createInitialComposerState = (args: {
    conversationId: string;
    sourceKey: string;
    managedSignature: string;
}): ComposerState => ({
    conversationId: args.conversationId,
    revision: 0,
    snippetRevision: 0,
    document: {
        body: '',
        signature: { kind: 'managed', sourceKey: args.sourceKey, text: args.managedSignature },
        latestAppliedSnippetId: undefined,
    },
});

/**
 * Splits a raw textarea value back into the body and the tracked signature region.
 * If the untouched managed suffix changes, the whole value becomes body and the
 * signature is dismissed so later signature actions cannot discard user-modified text.
 */
const splitTextEdit = (
    text: string,
    signature: SignatureState,
): { body: string; signature: SignatureState } => {
    const tracked = signatureText(signature);

    // No tracked signature (dismissed, or a blank managed region): it is all body.
    if (!tracked) {
        return { body: text, signature };
    }

    // The tracked signature still terminates the value: strip it, keep the same state.
    if (text.endsWith(tracked)) {
        return { body: text.slice(0, text.length - tracked.length), signature };
    }

    // The tracked suffix changed: preserve every character as body and dismiss it.
    return { body: text, signature: { kind: 'dismissed', sourceKey: signature.sourceKey } };
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const composerReducer = (state: ComposerState, action: ComposerAction): ComposerState => {
    switch (action.type) {
        case 'textEdited': {
            const { body, signature } = splitTextEdit(action.text, state.document.signature);
            return {
                ...state,
                revision: state.revision + 1,
                document: { ...state.document, body, signature },
            };
        }

        case 'snippetApplied':
            return {
                ...state,
                revision: state.revision + 1,
                snippetRevision: state.snippetRevision + 1,
                document: {
                    ...state.document,
                    body: prependBody(state.document.body, action.body),
                    latestAppliedSnippetId: action.snippetId,
                },
            };

        case 'suggestionAccepted':
            return {
                ...state,
                revision: state.revision + 1,
                document: {
                    ...state.document,
                    body: prependBody(state.document.body, action.content),
                },
            };

        case 'signatureToggled': {
            const { signature } = state.document;
            const nextSignature: SignatureState =
                signature.kind === 'dismissed' ?
                    { kind: 'managed', sourceKey: action.sourceKey, text: action.managedSignature } :
                    { kind: 'dismissed', sourceKey: action.sourceKey };
            return {
                ...state,
                revision: state.revision + 1,
                document: { ...state.document, signature: nextSignature },
            };
        }

        case 'signatureSourceChanged': {
            const { signature } = state.document;
            // A dismissed signature stays dismissed; a managed region is swapped for the new source's signature.
            const nextSignature: SignatureState =
                signature.kind === 'dismissed' ?
                    { kind: 'dismissed', sourceKey: action.sourceKey } :
                    { kind: 'managed', sourceKey: action.sourceKey, text: action.managedSignature };
            return {
                ...state,
                revision: state.revision + 1,
                document: { ...state.document, signature: nextSignature },
            };
        }

        case 'sendSucceeded': {
            if (state.conversationId !== action.conversationId) {
                return state;
            }

            const revisionMatches = state.revision === action.revision;
            const shouldClearSnippetAttribution =
                state.document.latestAppliedSnippetId !== undefined &&
                state.snippetRevision === action.snippetRevision;

            if (revisionMatches) {
                return {
                    conversationId: state.conversationId,
                    revision: state.revision + 1,
                    snippetRevision: state.snippetRevision,
                    document: {
                        body: '',
                        signature: { kind: 'managed', sourceKey: action.sourceKey, text: action.managedSignature },
                        latestAppliedSnippetId: shouldClearSnippetAttribution ?
                            undefined :
                            state.document.latestAppliedSnippetId,
                    },
                };
            }

            if (!shouldClearSnippetAttribution) {
                return state;
            }

            return {
                ...state,
                revision: state.revision + 1,
                document: { ...state.document, latestAppliedSnippetId: undefined },
            };
        }

        default:
            action satisfies never;
            return state;
    }
};
